import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiGrid, FiClock, FiStar, FiChevronRight, FiMic, FiPaperclip, FiZap, FiSearch, FiSettings, FiActivity } = FiIcons;

const Dashboard = () => {
  const templates = [
    { title: "Chest Pain Triage", category: "Cardiology", icon: "🫀", color: "bg-red-50" },
    { title: "Lab Interpreter", category: "General", icon: "🧪", color: "bg-blue-50" },
    { title: "Pediatric Fever", category: "Pediatrics", icon: "🤒", color: "bg-yellow-50" },
    { title: "Drug Interaction", category: "Pharmacy", icon: "💊", color: "bg-green-50" },
    { title: "Discharge Summary", category: "Ops", icon: "🏥", color: "bg-purple-50" }
  ];

  const recentProjects = [
    { name: "Cardiac Triage v2", updated: "2h ago", status: "Production", accuracy: "98.2%" },
    { name: "Lab Result Interpreter", updated: "1d ago", status: "Draft", accuracy: "N/A" },
    { name: "Pediatric Protocol", updated: "3d ago", status: "Testing", accuracy: "94.5%" }
  ];

  return (
    <div className="min-h-screen bg-brand-ivory pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl font-display font-bold text-brand-indigo mb-2">Creation Station</h1>
            <p className="text-brand-slate font-medium">Welcome, Dr. Sarah. Continue building your clinical pipeline.</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative hidden md:block">
              <input 
                type="text" 
                placeholder="Search projects..." 
                className="pl-11 pr-4 py-3 bg-white border-transparent rounded-2xl text-sm focus:ring-2 focus:ring-brand-mint transition-all w-64 shadow-sm"
              />
              <SafeIcon icon={FiSearch} className="absolute left-4 top-3.5 text-gray-400" />
            </div>
            <button className="p-3 bg-white text-brand-indigo rounded-2xl border border-gray-100 shadow-sm hover:bg-brand-indigo hover:text-white transition-all">
              <SafeIcon icon={FiSettings} />
            </button>
            <Link to="/builder" className="bg-brand-indigo text-white px-8 py-3 rounded-2xl font-bold flex items-center space-x-3 hover:bg-brand-slate transition-all shadow-lg active:scale-95">
              <SafeIcon icon={FiPlus} />
              <span>New Workflow</span>
            </Link>
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8 mb-12">
          <div className="lg:col-span-3">
            {/* Prompt Magic Section */}
            <div className="bg-brand-indigo rounded-[3rem] p-10 mb-12 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-mint opacity-10 rounded-full -mr-20 -mt-20 group-hover:scale-110 transition-transform duration-700" />
              <div className="relative z-10 max-w-2xl">
                <h2 className="text-2xl font-display font-bold text-white mb-6 flex items-center space-x-3">
                  <div className="w-10 h-10 bg-brand-mint rounded-xl flex items-center justify-center">
                    <SafeIcon icon={FiZap} className="text-brand-indigo" />
                  </div>
                  <span>Agentic Generation</span>
                </h2>
                <div className="relative">
                  <textarea 
                    className="w-full h-32 bg-white/10 border-white/10 rounded-[2rem] p-6 text-white placeholder:text-white/30 focus:ring-brand-mint transition-all resize-none font-medium"
                    placeholder='Describe your workflow: "A chest pain triage tool for the ER..."'
                  />
                  <button className="absolute bottom-4 right-4 bg-brand-mint text-brand-indigo px-6 py-2.5 rounded-xl font-bold hover:scale-105 transition-transform shadow-lg">
                    Generate
                  </button>
                </div>
              </div>
            </div>

            {/* Templates */}
            <div>
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-display font-bold text-brand-indigo">Start from Template</h3>
                <button className="text-brand-indigo text-sm font-bold flex items-center space-x-2">
                  <span>Explore Library</span>
                  <SafeIcon icon={FiChevronRight} />
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {templates.map((t, i) => (
                  <motion.div 
                    key={i} 
                    whileHover={{ y: -8, scale: 1.02 }}
                    className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-xl transition-all cursor-pointer group"
                  >
                    <div className={`w-14 h-14 ${t.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{t.icon}</span>
                    </div>
                    <div className="font-bold text-brand-indigo mb-1">{t.title}</div>
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t.category}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
              <h3 className="text-sm font-bold text-brand-indigo uppercase tracking-widest mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiActivity} className="text-brand-mint" />
                <span>Platform Health</span>
              </h3>
              <div className="space-y-4">
                {[
                  { label: "API Latency", val: "24ms" },
                  { label: "Credits Used", val: "47/100" },
                  { label: "Active Nodes", val: "12" }
                ].map(s => (
                  <div key={s.label} className="flex justify-between items-center">
                    <span className="text-xs text-brand-slate font-medium">{s.label}</span>
                    <span className="text-xs font-bold text-brand-indigo">{s.val}</span>
                  </div>
                ))}
              </div>
              <button className="w-full mt-6 py-3 bg-brand-ivory hover:bg-brand-mint/10 text-brand-indigo text-xs font-bold rounded-xl transition-colors">
                View Usage Docs
              </button>
            </div>

            {/* Recent Work */}
            <div>
              <h3 className="text-sm font-bold text-brand-indigo uppercase tracking-widest mb-6 flex items-center space-x-2">
                <SafeIcon icon={FiClock} />
                <span>Recent Projects</span>
              </h3>
              <div className="space-y-4">
                {recentProjects.map((p, i) => (
                  <motion.div 
                    key={i} 
                    whileHover={{ x: 5 }}
                    className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all group cursor-pointer"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-bold text-brand-indigo group-hover:text-brand-mint transition-colors truncate pr-2">{p.name}</div>
                      <div className={`text-[8px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${p.status === 'Production' ? 'bg-brand-mint/20 text-brand-indigo' : 'bg-gray-100 text-gray-400'}`}>
                        {p.status}
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[10px] text-gray-400 font-medium">Edited {p.updated}</span>
                      {p.accuracy !== 'N/A' && (
                        <div className="flex items-center space-x-1">
                          <SafeIcon icon={FiActivity} className="text-[8px] text-brand-mint" />
                          <span className="text-[10px] font-bold text-brand-indigo">{p.accuracy}</span>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;